import { CheckIcon, ClockIcon, ExclamationTriangleIcon, TaskIcon } from "@storytell/bsky-icons";
import type { Block } from "@storytell/streaming";
import { subscribeToBlockDeltas, subscribeToBlockStop } from "@storytell/streaming";
import { Show, createMemo, createSignal, onCleanup, onMount } from "solid-js";

interface TaskCardRendererProps {
  block: Block;
  onBlockClick?: () => void;
}

/**
 * Renders task-related blocks - task creation, updates, operation status.
 * Displays as cards with task-like UI elements including status and progress.
 */
export const TaskCardRenderer = (props: TaskCardRendererProps) => {
  const [content, setContent] = createSignal("");

  // Subscribe to delta events to get task content
  onMount(() => {
    const blockId = props.block.id;

    // Subscribe to delta events - get task description content
    const unsubscribeDelta = subscribeToBlockDeltas(blockId, (delta: string) => {
      setContent((prev) => prev + delta);
    });

    // Subscribe to block stop events
    const unsubscribeStop = subscribeToBlockStop(blockId, () => {
      // Block is complete
    });

    // Cleanup both subscriptions
    onCleanup(() => {
      unsubscribeDelta();
      unsubscribeStop();
    });
  });

  const taskStatus = createMemo(() => {
    const text = content().toLowerCase();
    if (text.includes("completed") || text.includes("done") || text.includes("finished")) {
      return "completed";
    }
    if (text.includes("failed") || text.includes("error") || text.includes("cancelled")) {
      return "failed";
    }
    if (text.includes("running") || text.includes("processing") || text.includes("executing")) {
      return "running";
    }
    if (text.includes("planned") || text.includes("scheduled") || text.includes("queued")) {
      return "planned";
    }
    return props.block.isComplete ? "completed" : "running";
  });

  const getStatusIcon = () => {
    switch (taskStatus()) {
      case "completed":
        return <CheckIcon size={16} class="text-green-600 dark:text-green-400" />;
      case "failed":
        return <ExclamationTriangleIcon size={16} class="text-red-600 dark:text-red-400" />;
      case "running":
        return <ClockIcon size={16} class="text-blue-600 dark:text-blue-400" />;
      default:
        return <TaskIcon size={16} class="text-gray-600 dark:text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (taskStatus()) {
      case "completed":
        return "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950";
      case "failed":
        return "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950";
      case "running":
        return "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950";
      default:
        return "border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950";
    }
  };

  return (
    <div
      data-renderer="task-card-renderer"
      class={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-all ${getStatusColor()}`}
      onClick={props.onBlockClick}
    >
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0 mt-1">
          <div class="w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
            {getStatusIcon()}
          </div>
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-2">
            <Show
              when={props.block.title}
              fallback={<span class="text-sm font-medium text-gray-700 dark:text-gray-300">Task Update</span>}
            >
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{props.block.title}</span>
            </Show>

            <span
              class={`px-2 py-1 text-xs rounded-full font-medium ${
                taskStatus() === "completed"
                  ? "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                  : taskStatus() === "failed"
                    ? "bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300"
                    : taskStatus() === "running"
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                      : "bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300"
              }`}
            >
              {taskStatus()}
            </span>
          </div>

          <Show when={props.block.activity}>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">{props.block.activity}</div>
          </Show>

          <Show when={content().trim()}>
            <div class="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{content()}</div>
          </Show>

          <Show when={!props.block.isComplete && taskStatus() === "running"}>
            <div class="flex items-center gap-2 mt-3">
              <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span class="text-xs text-blue-600 dark:text-blue-300">In progress...</span>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
};
